import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Loader2, Send, Copy, CheckCircle2, X, RefreshCw, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentOnboardingAPI } from '@/lib/api';

const isExpired = (link) => link.status === 'pending'
  && link.expires_at && new Date(link.expires_at).getTime() < Date.now();

const effectiveStatus = (link) => (isExpired(link) ? 'expired' : link.status);

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-200 text-slate-600',
  expired: 'bg-rose-100 text-rose-700',
};
const STATUS_LABELS = {
  pending: 'Awaiting agent',
  completed: 'Completed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

/**
 * KR-only panel listing every self-service onboarding invite and its
 * live status, so staff can see what was sent, resend a fresh link, or
 * cancel one that's still pending.
 */
export const OnboardingInvitesDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [freshLinks, setFreshLinks] = useState({}); // link_id -> onboard_link
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await agentOnboardingAPI.listLinks();
      setItems(data.items || []);
    } catch (_) {
      toast.error('Could not load onboarding invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { load(); setFreshLinks({}); } }, [open, load]);

  const resend = async (link) => {
    setBusyId(link.link_id);
    try {
      const { data } = await agentOnboardingAPI.resendLink(link.link_id);
      setFreshLinks((prev) => ({ ...prev, [link.link_id]: data.onboard_link }));
      toast.success(data.email_sent ? `Invite re-emailed to ${data.email}` : 'Fresh link ready — copy and share it');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend the invite');
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (link) => {
    setBusyId(link.link_id);
    try {
      await agentOnboardingAPI.cancelLink(link.link_id);
      toast.success('Invite cancelled');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel the invite');
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (linkId, url) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      setCopiedId(linkId);
      toast.success('Link copied');
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error('Could not copy — select the link manually');
    }
  };

  const pendingCount = items.filter((l) => effectiveStatus(l) === 'pending').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="onboarding-invites-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Inbox size={18} /> Onboarding Invites
          </DialogTitle>
          <DialogDescription>
            Every link sent to a new agent (including converted prospects) and its live status.
            Resend to email a fresh 7-day link, or cancel one that's still pending.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500" data-testid="invites-summary">
            {items.length} invite{items.length !== 1 ? 's' : ''} · {pendingCount} awaiting the agent
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="invites-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-2" data-testid="invites-list">
          {loading && items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500"><Loader2 size={18} className="animate-spin inline mr-2" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="invites-empty">No onboarding invites sent yet.</div>
          ) : items.map((link) => {
            const st = effectiveStatus(link);
            const fresh = freshLinks[link.link_id];
            const canResend = st !== 'completed';
            return (
              <div key={link.link_id} className="rounded-lg border border-slate-200 p-3" data-testid={`invite-row-${link.link_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">{link.email}</span>
                      <Badge className={`text-[10px] ${STATUS_STYLES[st] || 'bg-slate-100 text-slate-700'}`} data-testid={`invite-status-${link.link_id}`}>
                        {STATUS_LABELS[st] || st}
                      </Badge>
                      {link.source_prospect_id && <Badge className="text-[10px] bg-indigo-100 text-indigo-700">From prospect</Badge>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {link.agency_name} · NPN {link.npn} · sent {link.created_at ? new Date(link.created_at).toLocaleDateString() : '—'}
                      {link.created_by_email ? ` by ${link.created_by_email}` : ''}
                    </div>
                    {st === 'completed' && (
                      <div className="text-[11px] text-emerald-700 mt-0.5">
                        Agent created{link.created_agent_npn ? ` (NPN ${link.created_agent_npn})` : ''}{link.completed_at ? ` · ${new Date(link.completed_at).toLocaleDateString()}` : ''}
                      </div>
                    )}
                    {st === 'pending' && link.expires_at && (
                      <div className="text-[11px] text-slate-400 mt-0.5">Expires {new Date(link.expires_at).toLocaleDateString()}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canResend && (
                      <Button variant="outline" size="sm" className="h-8" onClick={() => resend(link)} disabled={busyId === link.link_id} data-testid={`invite-resend-${link.link_id}`}>
                        {busyId === link.link_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} className="mr-1" />}
                        Resend
                      </Button>
                    )}
                    {st === 'pending' && (
                      <Button variant="ghost" size="sm" className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => cancel(link)} disabled={busyId === link.link_id} data-testid={`invite-cancel-${link.link_id}`}>
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                </div>
                {fresh && (
                  <div className="flex gap-2 mt-2">
                    <Input readOnly value={fresh} className="text-xs h-8" data-testid={`invite-fresh-link-${link.link_id}`} />
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => copy(link.link_id, fresh)} data-testid={`invite-copy-${link.link_id}`}>
                      {copiedId === link.link_id ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingInvitesDialog;
